import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import { Meta } from '$partials/global';
import {
    ModuleDispatcher,
    ModuleSecondaryFeatureBlock
} from '$partials/modules';
import { Header, Footer, StickyCTAButtons } from '$partials/global/index.js';
import { slugify } from '../../utilities/slugify.js';
import { findModuleAnchors, getAllModules } from '$utilities/data';
import { useAnchor } from '$contexts/anchor';
import AnchorNavigation from '$partials/ui/anchor-navigation';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const CourseDetailPage = (props) => {
    const page = props.data.page;
    const newsletterModule = page?.modules?.find((item) => item.__typename === 'NewsletterBlock');
    const firstAccordion = page?.modules?.find((item) => item.__typename === 'AccordionBlock');

    // state
    const [selectedAccordionId, setSelectedAccordionId] = useState(firstAccordion?.id);

    const startTime = dayjs(page.startDate);
    const endTime = dayjs(page.endDate);

    const diff = endTime.diff(startTime, 'hours', true);
    const h = diff === 1 ? 'hour' : 'hours';

    // anchors
    const { setAnchors, anchors } = useAnchor();

    let anchorModules = [];
    const modules = getAllModules(page);

    useEffect(() => {
        if (modules) {
            anchorModules = anchorModules.concat(findModuleAnchors(modules));
            setAnchors(anchorModules);
        }
    }, []);

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                pageType={page?.__typename}
                standfirst={page?.standfirst} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main id='main-content'>
                <section className='layout-container pb-100'>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />
                        <div className='md:w-6/12 layout-grid-col-padding'>
                            {page && (
                                <p className='type-style-4 pb-30'>Course</p>
                            )}
                            {page?.title && (
                                <h1 className='type-style-2 pb-30 md:pb-35'>{page.title}</h1>
                            )}
                        </div>
                    </div>
                    <div className='layout-grid-negative-gutter flex flex-wrap md:flex-nowrap event-info pb-30 sm:pb-20 md:pb-50'>
                        <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />
                        {diff && (
                            <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                <p className='type-style-4 text-charcoal pb-10'>Time to complete</p>
                                <p className='type-style-4'>
                                    {`${diff} ${h}`}
                                </p>
                            </div>
                        )}
                        {page?.location && (
                            <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                <p className='type-style-4 text-charcoal pb-10'>Location</p>
                                <p className='type-style-4'>{page.location}</p>
                            </div>
                        )}
                        {page?.costMin === page?.costMax ? (
                            <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                <p className='type-style-4 text-charcoal pb-10'>Cost</p>
                                <p className='type-style-4'>
                                    {page.costMin === 0 ? 'Free' : `£${page.costMin}`}
                                </p>
                            </div>
                        ) : (
                            <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                <p className='type-style-4 text-charcoal pb-10'>Cost</p>
                                <p className='type-style-4'>
                                    {`£${page.costMin}`} - {`£${page.costMax}`}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />
                        <div className='md:w-11/12 layout-grid-col-padding'>
                            {page?.ctaUrl && (
                                <a
                                    className={`pill-button gtm-${slugify(page.ctaLabel)}`}
                                    href={page?.ctaUrl}>{page.ctaLabel}</a>
                            )}
                        </div>
                    </div>
                </section>

                {/* Anchor Navigation */}
                {anchors?.length > 0 && (
                    <AnchorNavigation
                        anchors={anchors} />
                )}

                <div className='bg-white relative'>
                    {page?.modules?.map((module, idx) => {
                        if (module.__typename === 'AccordionBlock') {
                            module.isSelectedAccordion = (module.id === selectedAccordionId);
                            module.setIsSelectedAccordion = setSelectedAccordionId;
                        }

                        if (module.__typename === 'NewsletterBlock' || module.__typename === 'CalendarBlock') {
                            module.borderTop = true;
                        }

                        return (
                            <ModuleDispatcher
                                index={idx}
                                isDetailPage={true}
                                key={idx}
                                {...module} />
                        );
                    })}
                    {page?.secondaryFeature?.[0] && (
                        <>
                            <div className='border-t border-t-charcoal' />
                            <ModuleSecondaryFeatureBlock {...page?.secondaryFeature?.[0]} />
                        </>
                    )}
                </div>
            </main>

            <Footer
                hasNewsletterModule={newsletterModule}
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
    }),
    domain: PropTypes.string,
};

CourseDetailPage.propTypes = propTypes;

export default CourseDetailPage;
